import { FunctionComponent, useState, useEffect, useRef } from "react";
import {
  IonButton,
  IonCheckbox,
  IonContent,
  IonGrid,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPopover,
  IonProgressBar,
  IonRow,
  IonText,
} from "@ionic/react";
import { close, cloudDownload, cloudUpload } from "ionicons/icons";
import { BuildingItemModel } from "../models/buildingItem.model";
import { useUserContext } from "../../user/contexts/user.context";
import { Sites } from "../../service-worker/sites";
import "./download-popover.component.scss";

interface DownloadPopoverProps {
  downloadButtonLabel: string;
  cancelButtonLabel: string;
  timingNoticeLabel: string;
  contentLabel: string;
  titleLabel: string;
  downloadInProgress: boolean;
  closePopover: () => void;
  isPopoverOpened: boolean;
  launchDownload: (selectedBuildingIds: string[], downloadOutdoorAreas: boolean) => void;
  launchSync: (selectedBuildingIds?: string[], syncOutdoorAreas?: boolean) => void;
  buildings: BuildingItemModel[];
  siteId: string;
  hasOutdoorAreas: boolean;
  outdoorAreasTitleLabel: string;
  syncButtonLabel: string;
  synchronizingLabel: string;
  synchronizationState: boolean;
}

export const DownloadPopover: FunctionComponent<DownloadPopoverProps> = (props) => {
  const [selectedBuildingIds, setSelectedBuildingIds] = useState<Set<string>>(new Set());
  const [selectedSyncBuildingIds, setSelectedSyncBuildingIds] = useState<Set<string>>(new Set());
  const [offlineBuildings, setOfflineBuildings] = useState<BuildingItemModel[]>([]);
  const [onlineBuildings, setOnlineBuildings] = useState<BuildingItemModel[]>([]);
  const [downloadOutdoorAreas, setDownloadOutdoorAreas] = useState<boolean>(false);
  const [syncOutdoorAreas, setSyncOutdoorAreas] = useState<boolean>(false);
  const [isOutdoorAreasOffline, setIsOutdoorAreasOffline] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const { currentOrganization } = useUserContext();
  const prevSyncStateRef = useRef(props.synchronizationState);

  // Détecter la fin de la synchronisation pour forcer un rafraîchissement
  useEffect(() => {
    if (prevSyncStateRef.current && !props.synchronizationState) {
      // La synchronisation vient de se terminer
      setRefreshKey(prev => prev + 1);
    }
    prevSyncStateRef.current = props.synchronizationState;
  }, [props.synchronizationState]);

  // Séparer les bâtiments en téléchargés et non téléchargés et vérifier l'espace extérieur
  // Réinitialiser les états quand le site change pour éviter d'afficher des données d'un autre site
  useEffect(() => {
    // Réinitialiser immédiatement les états quand le site change
    setOfflineBuildings([]);
    setOnlineBuildings([]);
    setIsOutdoorAreasOffline(false);
    setSelectedBuildingIds(new Set());
    setSelectedSyncBuildingIds(new Set());
    setDownloadOutdoorAreas(false);
    setSyncOutdoorAreas(false);
  }, [props.siteId]);

  useEffect(() => {
    async function checkBuildingsStatus() {
      if (!props.isPopoverOpened || !currentOrganization) {
        setOfflineBuildings([]);
        setOnlineBuildings([]);
        setIsOutdoorAreasOffline(false);
        return;
      }

      // Petit délai pour s'assurer que le cache du service worker est à jour
      await new Promise(resolve => setTimeout(resolve, 100));

      const offline: BuildingItemModel[] = [];
      const online: BuildingItemModel[] = [];

      // Vérifier uniquement les bâtiments du site actuel (props.buildings devrait déjà être filtré par site)
      await Promise.all([
        ...props.buildings.map(async (building) => {
          // Vérifier que le bâtiment appartient bien au site actuel
          const isOffline = await Sites.isBuildingOffline(currentOrganization.code, props.siteId, building.id);
          if (isOffline) {
            offline.push(building);
          } else {
            online.push(building);
          }
        }),
        (async () => {
          if (props.hasOutdoorAreas) {
            // Vérifier uniquement l'espace extérieur du site actuel
            const outdoorOffline = await Sites.isOutdoorAreasOffline(currentOrganization.code, props.siteId);
            setIsOutdoorAreasOffline(outdoorOffline);
          } else {
            setIsOutdoorAreasOffline(false);
          }
        })()
      ]);

      setOfflineBuildings(offline);
      setOnlineBuildings(online);
      // Par défaut, aucun bâtiment n'est sélectionné
      setSelectedBuildingIds(new Set());
      // Par défaut, aucun bâtiment téléchargé n'est sélectionné pour la synchronisation
      setSelectedSyncBuildingIds(new Set());
      // Par défaut, l'espace extérieur n'est pas sélectionné pour le téléchargement
      setDownloadOutdoorAreas(false);
    }
    checkBuildingsStatus();
  }, [props.isPopoverOpened, props.buildings, props.siteId, props.hasOutdoorAreas, currentOrganization, props.synchronizationState, refreshKey]);

  // Mettre à jour syncOutdoorAreas quand isOutdoorAreasOffline change (décoché par défaut)
  useEffect(() => {
    setSyncOutdoorAreas(false);
  }, [isOutdoorAreasOffline]);

  const toggleBuilding = (buildingId: string) => {
    setSelectedBuildingIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(buildingId)) {
        newSet.delete(buildingId);
      } else {
        newSet.add(buildingId);
      }
      return newSet;
    });
  };

  const toggleSyncBuilding = (buildingId: string) => {
    setSelectedSyncBuildingIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(buildingId)) {
        newSet.delete(buildingId);
      } else {
        newSet.add(buildingId);
      }
      return newSet;
    });
  };

  const handleDownload = () => {
    props.launchDownload(Array.from(selectedBuildingIds), downloadOutdoorAreas);
  };

  const handleSync = () => {
    // Si aucun bâtiment n'est sélectionné, synchroniser tout le site (comme l'ancien bouton)
    // Sinon, synchroniser uniquement les bâtiments sélectionnés
    const buildingIdsToSync = selectedSyncBuildingIds.size > 0 
      ? Array.from(selectedSyncBuildingIds) 
      : undefined;
    props.launchSync(buildingIdsToSync, syncOutdoorAreas);
  };

  return (
  <IonPopover role="dialog" backdropDismiss={false} isOpen={props.isPopoverOpened} onDidDismiss={props.closePopover}>
    <IonContent>
        <IonGrid className="ion-no-padding" style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        <IonRow
          className="ion-padding-start ion-justify-content-between ion-align-items-center"
            style={{ marginTop: "0.8em", flexShrink: 0 }}
        >
          <IonText role={"heading"} style={{ height: "35px", lineHeight: "35px" }}>
            {props.titleLabel}
          </IonText>
          {!props.downloadInProgress && (
              <IonButton onClick={props.closePopover} role="button" color="dark" fill="clear" size="small">
              <IonIcon slot="icon-only" icon={close} />
            </IonButton>
          )}
        </IonRow>
          <div style={{ flex: "1 1 auto", overflowY: "auto", overflowX: "hidden", minHeight: 0, maxHeight: "calc(75vh - 200px)" }}>
        <IonRow id="popover-download" className="ion-padding-horizontal">
          <p>{props.contentLabel}</p>
        </IonRow>
            {!props.downloadInProgress && (onlineBuildings.length > 0 || (props.hasOutdoorAreas && !isOutdoorAreasOffline)) && (
              <IonRow className="ion-padding-horizontal">
                <IonList className="download-popover-buildings-list" style={{ width: "100%", paddingLeft: "0.75em" }}>
                  {props.hasOutdoorAreas && !isOutdoorAreasOffline && (
                    <IonItem style={{ "--padding-start": "0.75em" } as any}>
                      <IonCheckbox
                        checked={downloadOutdoorAreas}
                        onIonChange={() => setDownloadOutdoorAreas(!downloadOutdoorAreas)}
                        slot="start"
                        style={{ marginRight: "0.75em" }}
                      />
                      <IonLabel>{props.outdoorAreasTitleLabel}</IonLabel>
                    </IonItem>
                  )}
                  {onlineBuildings.map((building) => (
                    <IonItem key={building.id} style={{ "--padding-start": "0.75em" } as any}>
                      <IonCheckbox
                        checked={selectedBuildingIds.has(building.id)}
                        onIonChange={() => toggleBuilding(building.id)}
                        slot="start"
                        style={{ marginRight: "0.75em" }}
                      />
                      <IonLabel>{building.name}</IonLabel>
                    </IonItem>
                  ))}
                </IonList>
              </IonRow>
            )}
        {props.downloadInProgress ? (
          <IonRow className="ion-padding-horizontal ion-justify-content-center" style={{ paddingBottom: 5 }}>
            <IonIcon
              color="medium"
              style={{ height: "60px", width: "60px" }}
              icon={cloudDownload}
              className="ion-margin-vertical"
            />
            <IonProgressBar color={"secondary"} type="indeterminate" />
            <IonText style={{ fontSize: ".8rem" }} color="medium" className="ion-margin-vertical">
              {props.timingNoticeLabel}
            </IonText>
          </IonRow>
        ) : (
              <>
          <IonRow className="ion-padding-horizontal ion-justify-content-center" style={{ paddingBottom: 5 }}>
            <IonButton
              data-testid="download-button"
              role="button"
              aria-label={props.downloadButtonLabel}
              color={"secondary"}
                    onClick={handleDownload}
                    disabled={selectedBuildingIds.size === 0 && !downloadOutdoorAreas}
            >
              <IonIcon slot="start" icon={cloudDownload} />
              <IonLabel>{props.downloadButtonLabel}</IonLabel>
            </IonButton>
          </IonRow>
                {(offlineBuildings.length > 0 || isOutdoorAreasOffline) && (
                  <>
                    <IonRow className="ion-padding-horizontal download-popover-sync-section">
                      <IonText style={{ fontSize: "0.9rem", fontWeight: "bold", marginBottom: "0.5em", display: "block" }}>
                        Bâtiments téléchargés
                      </IonText>
                      <IonList className="download-popover-buildings-list" style={{ width: "100%", paddingLeft: "0.75em" }}>
                        {isOutdoorAreasOffline && (
                          <IonItem style={{ "--padding-start": "0.75em" } as any}>
                            <IonCheckbox
                              checked={syncOutdoorAreas}
                              onIonChange={() => setSyncOutdoorAreas(!syncOutdoorAreas)}
                              slot="start"
                              style={{ marginRight: "0.75em" }}
                            />
                            <IonLabel>{props.outdoorAreasTitleLabel}</IonLabel>
                          </IonItem>
                        )}
                        {offlineBuildings.map((building) => (
                          <IonItem key={building.id} style={{ "--padding-start": "0.75em" } as any}>
                            <IonCheckbox
                              checked={selectedSyncBuildingIds.has(building.id)}
                              onIonChange={() => toggleSyncBuilding(building.id)}
                              slot="start"
                              style={{ marginRight: "0.75em" }}
                            />
                            <IonLabel>{building.name}</IonLabel>
                          </IonItem>
                        ))}
                      </IonList>
                    </IonRow>
                  </>
                )}
              </>
            )}
          </div>
          {(offlineBuildings.length > 0 || isOutdoorAreasOffline) && !props.downloadInProgress && (
            <div className="download-popover-sync-button-container">
              <IonRow className="ion-padding-horizontal ion-justify-content-center">
                {props.synchronizationState ? (
                  <>
                    <IonProgressBar color={"tertiary"} type="indeterminate" style={{ width: "100%", marginBottom: "0.5em" }} />
                    <IonText style={{ fontSize: ".8rem" }} color="medium">
                      {props.synchronizingLabel}
                    </IonText>
                  </>
                ) : (
                  <IonButton
                    data-testid="sync-button"
                    role="button"
                    aria-label={props.syncButtonLabel}
                    color={"tertiary"}
                    onClick={handleSync}
                    disabled={props.synchronizationState || (selectedSyncBuildingIds.size === 0 && !syncOutdoorAreas)}
                  >
                    <IonIcon slot="start" icon={cloudUpload} />
                    <IonLabel>{props.syncButtonLabel}</IonLabel>
                  </IonButton>
                )}
              </IonRow>
            </div>
        )}
      </IonGrid>
    </IonContent>
  </IonPopover>
);
};
